import { transform, TransformMethods, Dictionary } from 'data/transform'
import { UserModel } from 'data/models'
import { WorkawareApi } from 'apis/WorkawareApi'
import { PasswordChangeRequest, PasswordUpdateRequest, PasswordGenerationRequest } from 'data/dtos'
import { PagedResult, PagedArguments } from 'types/apis'

export class userService {
  private _api: WorkawareApi
  private _userTransform: TransformMethods<UserModel>

  constructor(api: WorkawareApi) {
    this._api = api
    this._userTransform = transform<UserModel>(UserModel)
  }

  getUser = (id: number) => {
    return new Promise<UserModel>((resolve, reject) => {
      this._api.user
        .get(id)
        .then((result) => resolve(this._userTransform.from(result)))
        .catch((error) => reject(error))
    })
  }

  getUsers = (ids: number[]): Promise<any[]> => {
    return this._api.user.batch(ids)
  }

  getAllUsers = () => {
    return new Promise<Array<UserModel>>((resolve, reject) => {
      this._api.user
        .all()
        .then((result) => resolve(this._userTransform.arrayFrom(result.value)))
        .catch((error) => reject(error))
    })
  }

  getPagedUsers = (args: PagedArguments) => {
    return new Promise<PagedResult<UserModel>>((resolve, reject) => {
      this._api.user
        .paged(args)
        .then((result) => resolve(result))
        .catch((error) => reject(error))
    })
  }

    getInactivePagedUsers = (args: PagedArguments) => {
    return new Promise<PagedResult<UserModel>>((resolve, reject) => {
      this._api.user
        .inActivePaged(args)
        .then((result) => resolve(result))
        .catch((error) => reject(error))
    })
  }

  getActiveUsers = () => {
    return new Promise<Array<UserModel>>((resolve, reject) => {
      this._api.user
        .active()
        .then((result) => resolve(this._userTransform.arrayFrom(result.value)))
        .catch((error) => reject(error))
    })
  }
  getActiveUsersLookup = () => {
    return new Promise<Dictionary<UserModel>>((resolve, reject) => {
      this._api.user
        .active()
        .then((result) => resolve(this._userTransform.mapFrom(result.value, 'Id')))
        .catch((error) => reject(error))
    })
  }

  createUser = (user: UserModel) => {
    return new Promise<UserModel>((resolve, reject) => {
      this._api.user
        .create(user)
        .then((user) => resolve(this._userTransform.from(user)))
        .catch((error) => reject(error))
    })
  }

  updatePostPickListUser = (id: number) => {
    return new Promise<UserModel>((resolve, reject) => {
      this._api.user
          .updatePostPickList(id)
    })
  }

  deleteUser = (id: number) => {
    return new Promise<void>((resolve, reject) => {
      this._api.user.delete(id)
        .then(() => {
          resolve(); 
        })
        .catch((error) => {
          reject(error); 
        });
    });
  }

  updateDeletePickListUser = (id: number) => {
    return new Promise<UserModel>((resolve, reject) => {
      this._api.user
          .updateDeletePickList(id)
    })
  }

  updateUser = (id: number, user: Dictionary<any>) => {
    return new Promise((resolve, reject) => {
      this._api.user
        .update(id, user)
        .then((user) => resolve(this._userTransform.from(user)))
        .catch((error) => reject(error))
    })
  }

  updatePutPickListUser = (id: number) => {
    return new Promise<UserModel>((resolve, reject) => {
      this._api.user
          .updatePutPickList(id)
    })
  }

  updatePassword = (id: number, password: string,newUser: boolean,): Promise<void> => {
    return new Promise((resolve, reject) => {
      const dto = new PasswordUpdateRequest()
      dto.userId = id
      dto.password = password
      dto.newUser = newUser
      this._api.user
        .updatePassword(dto)
        .then(() => resolve())
        .catch((error) => reject(error))
    })
  }

  changePassword = (currentPassword: string, newPassword: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const dto = new PasswordChangeRequest()
      dto.currentPassword = currentPassword
      dto.newPassword = newPassword
      this._api.user
        .changePassword(dto)
        .then(() => resolve())
        .catch((error) => reject(error))
    })
  }

  generatePassword = (id: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      const dto = new PasswordGenerationRequest()
      dto.userId = id
      this._api.user
        .generatePassword(dto)
        .then(() => resolve())
        .catch((error) => reject(error))
    })
  }

  uploadPhoto = async (data: FormData, name: string) => {
    await this._api.user.uploadPhoto(data, name)
  }

  userExists = async (username: string) => {
    return await this._api.user.checkUsername(username)
  }

    personalEmailExists = async (personalEmail: string) => {
    return await this._api.user.checkPersonalEmail(personalEmail)
  }

    workEmailExists = async (workEmail: string) => {
    return await this._api.user.checkWorkEmail(workEmail)
  }

  getNotificationPreferences = (): Promise<NotificationPreferencesDto> => {
    return this._api.user.getNotificationPreferences()
  }

  saveNotificationPreferences = (data: NotificationPreferencesDto): Promise<NotificationPreferencesDto> => {
    return this._api.user.saveNotificationPreferences(data)
  }

  getSystemPreferences = (): Promise<SystemPreferencesDto> => {
    return this._api.user.getSystemPreferences()
  }

  saveSystemPreferences = (data: SystemPreferencesDto): Promise<SystemPreferencesDto> => {
    return this._api.user.saveSystemPreferences(data)
  }

  getTwoFactorSettings = (): Promise<any> => {
    return this._api.user.getTwoFactorSettings()
  }

  updateTwoFactorSettings = (enabled: boolean, mode: string): Promise<any> => {
    return this._api.user.updateTwoFactorSettings({ enabled, mode })
  }

  getSessionTimeoutSettings = (): Promise<SessionTimeoutSettingsDto> => {
    return this._api.user.getSessionTimeoutSettings()
  }

  saveSessionTimeoutSettings = (data: SessionTimeoutSettingsDto): Promise<SessionTimeoutSettingsDto> => {
    return this._api.user.saveSessionTimeoutSettings(data)
  }
}

export interface NotificationPreferencesDto {
  SendEmailsDuringSpecificTimes: boolean
  StartTime: string | null
  EndTime: string | null
  SendEmailsOnWeekends: boolean
}

export interface SystemPreferencesDto {
  TimeZone: string | null
  DateFormat: string | null
  TimeFormat: string | null
  DarkMode: boolean
}

export interface SessionTimeoutSettingsDto {
  IsEnabled: boolean
  IsActive: boolean
  InactivityTimeoutMinutes: number
  LogoutWarningEnabled: boolean
  WarningTimerMinutes: number
  CustomMessageEnabled: boolean
  CustomMessage: string | null
}
